<template>
  <div v-if="aiAccess === true" class="global-ai-chat">
    <transition name="ai-chat-fade">
      <div
        v-if="panelOpen"
        ref="panel"
        class="ai-chat-panel card shadow"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ai-disclosure-notice ai-chat-title"
        tabindex="-1"
        :style="panelStyle"
        @keydown.esc="panelOpen = false"
        @keydown="onPanelKeydown"
      >
        <!-- Resize handles are mouse-only affordances; hidden from AT so they do not
             inflate VoiceOver's item count when focus enters the dialog. -->
        <!-- Top resize handle -->
        <div
          class="ai-chat-resize-handle ai-chat-resize-handle--top"
          aria-hidden="true"
          @mousedown="startResize('top', $event)"
        />
        <!-- Left resize handle -->
        <div
          class="ai-chat-resize-handle ai-chat-resize-handle--left"
          aria-hidden="true"
          @mousedown="startResize('left', $event)"
        />
        <!-- Top-left corner handle -->
        <div
          class="ai-chat-resize-handle ai-chat-resize-handle--corner"
          aria-hidden="true"
          @mousedown="startResize('corner', $event)"
        >
          <svg
            class="ai-chat-resize-corner-icon"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <!-- vertical arrow -->
            <line x1="12" y1="2" x2="12" y2="22" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            <polyline points="8,6 12,2 16,6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
            <polyline points="8,18 12,22 16,18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
          </svg>
        </div>

        <div class="ai-chat-panel-header card-header d-flex align-items-center justify-content-between py-2">
          <h2
            id="ai-chat-title"
            ref="title"
            class="ai-chat-title font-weight-bold mb-0"
            tabindex="-1"
          >
            AI assistant
          </h2>
          <div class="ai-chat-header-actions d-flex align-items-center">
            <b-button
              size="sm"
              variant="outline-secondary"
              class="ai-chat-new-chat-btn"
              aria-label="Start new conversation"
              @click="onNewConversation"
            >
              New chat
            </b-button>
            <b-button
              size="sm"
              variant="link"
              class="ai-chat-close-header-btn"
              aria-label="Close AI assistant"
              @click="panelOpen = false"
            >
              <span class="ai-chat-close-header-icon" aria-hidden="true">&times;</span>
            </b-button>
          </div>
        </div>

        <!-- Persistent AI Disclosure Notice (EU AI Act Art. 50 compliance - visible at every turn) -->
        <div
          id="ai-disclosure-notice"
          class="ai-chat-disclosure-notice px-3 py-2 bg-light border-bottom d-flex align-items-center"
          role="note"
        >
          <span class="badge badge-secondary mr-2" aria-hidden="true">AI</span>
          <span>You are chatting with an AI assistant.</span>
        </div>

        <div
          ref="scrollArea"
          class="ai-chat-messages"
          role="log"
          aria-live="polite"
          aria-label="Chat history"
        >
          <div
            v-if="!messages.length"
            class="ai-chat-empty px-4 py-4 text-center"
          >
            Ask about events, the community, or anything else it can help with.
          </div>
          <ChatMessageBubbleRow
            v-for="msg in messages"
            :key="msg.id"
            :is-own="msg.role === 'user'"
            :text="msg.text"
          />
        </div>
        <div v-if="error" class="ai-chat-error small px-3 text-danger" role="alert">
          {{ error }}
        </div>
        <ChatMessageComposer
          ref="composer"
          :key="composerKey"
          v-model="draft"
          :disabled="isSending"
          :send-disabled="isSending || !draftTrimmed"
          :sending-label="isSending ? 'Sending…' : 'Send'"
          placeholder="Type your message here"
          @send="onSend"
        />
      </div>
    </transition>

    <button
      v-if="!panelOpen"
      type="button"
      class="ai-chat-launcher ai-chat-launcher-pill"
      aria-label="Open Chat AI assistant"
      @click="openPanel"
    >
      <span class="ai-chat-launcher-shimmer" aria-hidden="true" />
      <img
        class="ai-chat-launcher-btn-img"
        :src="aiButtonSrc"
        alt="Need Help? Chat with AI Assistant"
        decoding="async"
      />
    </button>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import SocketConnect from '../../utils/socket';
import { resolveEventIdForAiChat } from '../../utils/aiChatContext';
import ChatMessageBubbleRow from '../messages/ChatMessageBubbleRow.vue';
import ChatMessageComposer from '../messages/ChatMessageComposer.vue';

const MIN_WIDTH = 340;
const MIN_HEIGHT = 320;
const DEFAULT_WIDTH = 500;
const DEFAULT_HEIGHT = 600;

export default {
  name: 'GlobalAiChat',
  components: {
    ChatMessageBubbleRow,
    ChatMessageComposer,
  },
  data() {
    return {
      panelOpen: false,
      draft: '',
      composerKey: 0,
      aiButtonSrc: require('@/assets/img/ai-button.png'),
      panelWidth: DEFAULT_WIDTH,
      panelHeight: DEFAULT_HEIGHT,
      resizing: null,
      lastFocused: null,
    };
  },
  computed: {
    ...mapGetters('aiChat', ['messages', 'isSending', 'error', 'aiAccess']),
    ...mapGetters('events', {
      storeEvent: 'fetchEvent',
    }),
    draftTrimmed() {
      return (this.draft || '').trim();
    },
    routeEventId() {
      return resolveEventIdForAiChat(this.$route, this.storeEvent);
    },
    scrollProbe() {
      const list = this.messages;
      if (!list.length) return '0';
      const last = list[list.length - 1];
      return `${list.length}:${last.id}:${(last.text && last.text.length) || 0}`;
    },
    panelStyle() {
      const maxW = window.innerWidth - 32;
      const maxH = window.innerHeight - 96;
      const w = Math.min(this.panelWidth, maxW);
      const h = Math.min(this.panelHeight, maxH);
      return {
        width: `${w}px`,
        maxWidth: 'none',
        height: `${h}px`,
        maxHeight: 'none',
      };
    },
  },
  watch: {
    scrollProbe() {
      this.$nextTick(() => this.scrollToBottom());
    },
    isSending(next, prev) {
      if (prev === true && next === false) {
        this.$nextTick(() => this.scrollToBottom());
      }
    },
    panelOpen(open) {
      if (open) {
        // Remember the trigger so focus can return to it when the panel closes.
        this.lastFocused = document.activeElement;
        this.$nextTick(() => {
          this.scrollToBottom();
          // Focus the heading. Screen readers announce the dialog boundary before the
          // focused element, and the dialog is named disclosure-first via
          // aria-labelledby, so the AI disclosure is the first thing spoken.
          const target = this.$refs.title || this.$refs.panel;
          if (target) target.focus();
        });
      } else {
        this.$nextTick(() => this.restoreFocus());
      }
    },
  },
  mounted() {
    this.checkAccess();
    this.restoreFromStorage();
    window.addEventListener('keydown', this.handleGlobalKeydown);
  },
  beforeDestroy() {
    this.stopResize();
    window.removeEventListener('keydown', this.handleGlobalKeydown);
    if (this.panelOpen) this.restoreFocus();
  },
  methods: {
    ...mapActions('aiChat', [
      'sendMessage',
      'startNewConversation',
      'restoreFromStorage',
      'checkAccess',
    ]),
    handleGlobalKeydown(e) {
      if (e.key === 'Escape' && this.panelOpen) {
        this.panelOpen = false;
      }
    },
    restoreFocus() {
      const previous = this.lastFocused;
      this.lastFocused = null;
      if (previous && document.body.contains(previous) && typeof previous.focus === 'function') {
        previous.focus();
        return;
      }
      // The launcher is swapped by v-if on close, so the remembered node may be gone.
      const launcher = document.querySelector('.ai-chat-launcher');
      if (launcher) launcher.focus();
    },
    onPanelKeydown(e) {
      if (e.key !== 'Tab' || !this.$refs.panel) return;
      const nodes = this.$refs.panel.querySelectorAll(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      const items = Array.from(nodes).filter((n) => n.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === this.$refs.panel)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    },
    ensureSocketConnected() {
      const socket = new SocketConnect();
      if (socket && socket.disconnected && typeof socket.connect === 'function') {
        socket.connect();
      }
    },
    openPanel() {
      this.ensureSocketConnected();
      this.panelOpen = true;
    },
    onNewConversation() {
      this.startNewConversation();
      this.draft = '';
    },
    onSend() {
      if (!this.draftTrimmed || this.isSending) return;
      const text = this.draftTrimmed;
      this.draft = '';
      this.composerKey += 1;
      this.ensureSocketConnected();
      const eventId = this.routeEventId;
      this.sendMessage({
        text,
        eventId: eventId || undefined,
      });
    },
    scrollToBottom() {
      const el = this.$refs.scrollArea;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    },
    startResize(direction, e) {
      e.preventDefault();
      const startX = e.clientX;
      const startY = e.clientY;
      const startW = this.panelWidth;
      const startH = this.panelHeight;

      const onMove = (ev) => {
        const dx = startX - ev.clientX;
        const dy = startY - ev.clientY;
        if (direction === 'left' || direction === 'corner') {
          this.panelWidth = Math.max(MIN_WIDTH, startW + dx);
        }
        if (direction === 'top' || direction === 'corner') {
          this.panelHeight = Math.max(MIN_HEIGHT, startH + dy);
        }
      };

      const onUp = () => this.stopResize();

      this.resizing = { onMove, onUp };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    stopResize() {
      if (this.resizing) {
        window.removeEventListener('mousemove', this.resizing.onMove);
        window.removeEventListener('mouseup', this.resizing.onUp);
        this.resizing = null;
      }
    },
  },
};
</script>

<style scoped>
.global-ai-chat {
  position: fixed;
  z-index: 1080;
  right: 1rem;
  bottom: 1rem;
  font-family: 'Montserrat', sans-serif;
}

/* AI Chat FAB Launcher */
.ai-chat-launcher {
  position: relative;
  cursor: pointer;
  overflow: hidden;
  color: #1d1d1d;
  flex-shrink: 0;
  border: none;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.ai-chat-launcher-pill {
  height: 3.5rem;
  border-radius: 9999px;
  box-shadow:
    0 4px 18px rgba(0, 0, 0, 0.12),
    0 2px 6px rgba(189, 157, 95, 0.25);
  background-color: #ffffff;
}

.ai-chat-launcher-pill:hover {
  transform: translateY(-2px);
  box-shadow:
    0 6px 22px rgba(0, 0, 0, 0.18),
    0 3px 8px rgba(189, 157, 95, 0.35);
}

.ai-chat-launcher-pill:active {
  transform: translateY(0);
}

.ai-chat-launcher-pill:focus {
  outline: none;
  box-shadow:
    0 4px 18px rgba(189, 157, 95, 0.55),
    0 0 0 3px rgba(189, 157, 95, 0.35);
}

.ai-chat-launcher-btn-img {
  position: relative;
  z-index: 1;
  display: block;
  height: 3.5rem;
  width: auto;
  object-fit: contain;
  pointer-events: none;
}

.ai-chat-launcher-shimmer {
  position: absolute;
  top: -60%;
  left: -60%;
  width: 220%;
  height: 220%;
  background: linear-gradient(
    105deg,
    transparent 38%,
    rgba(255, 255, 255, 0) 42%,
    rgba(255, 255, 255, 0.55) 50%,
    rgba(255, 255, 255, 0) 58%,
    transparent 62%
  );
  animation: ai-chat-shimmer 2.75s ease-in-out infinite;
  pointer-events: none;
}

@keyframes ai-chat-shimmer {
  0% {
    transform: translateX(-35%) rotate(18deg);
  }
  100% {
    transform: translateX(35%) rotate(18deg);
  }
}

.ai-chat-panel {
  position: absolute;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 0.5rem;
  border: 1px solid #dee2e6;
  /* width/height/maxWidth/maxHeight controlled via inline panelStyle */
}

/* Focused programmatically (tabindex="-1"); no ring for non-operable nodes. */
.ai-chat-panel:focus,
.ai-chat-title:focus {
  outline: none;
}

/* Resize handles */
.ai-chat-resize-handle {
  position: absolute;
  z-index: 10;
}

.ai-chat-resize-handle--left {
  top: 12px;
  bottom: 12px;
  left: 0;
  width: 6px;
  cursor: ew-resize;
}

.ai-chat-resize-handle--top {
  top: 0;
  left: 12px;
  right: 12px;
  height: 6px;
  cursor: ns-resize;
}

.ai-chat-resize-handle--corner {
  top: 0;
  left: 0;
  width: 20px;
  height: 20px;
  cursor: nwse-resize;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ai-chat-resize-corner-icon {
  color: #495057;
  transform: rotate(135deg);
  pointer-events: none;
  transition: color 0.15s;
}

.ai-chat-resize-handle--corner:hover .ai-chat-resize-corner-icon {
  color: #2f2a22;
}

.ai-chat-resize-handle--left:hover,
.ai-chat-resize-handle--top:hover {
  background: rgba(189, 157, 95, 0.25);
  border-radius: 3px;
}

.ai-chat-panel-header .ai-chat-title {
  font-size: 1.15rem;
  margin-bottom: 0;
}

.ai-chat-header-actions {
  gap: 0.35rem;
}

/* Bootstrap's secondary grey (#6c757d) is only 4.37:1 on the header background;
   #495057 clears WCAG AA on every surface in this panel. */
.ai-chat-header-actions .ai-chat-close-header-btn {
  color: #495057;
}

.ai-chat-header-actions .ai-chat-close-header-btn:hover,
.ai-chat-header-actions .ai-chat-close-header-btn:focus {
  color: #2f2a22;
}

.ai-chat-close-header-btn {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  min-width: 2.5rem;
  min-height: 2.5rem;
  padding: 0 !important;
  line-height: 1;
}

.ai-chat-close-header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  line-height: 1;
  font-weight: 200;
  margin: 0;
  transform: translateY(-0.04em);
}

.ai-chat-disclosure-notice {
  font-size: 18px;
  line-height: 1.4;
  font-weight: 500;
  color: #495057;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  padding: 0.55rem 0.9rem;
  flex-shrink: 0;
  user-select: none;
}

.ai-chat-disclosure-notice .badge {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.4px;
  padding: 0.3em 0.55em;
  background-color: #495057;
  color: #fff;
  border-radius: 4px;
}

.ai-chat-new-chat-btn {
  font-size: 14px !important;
  padding: 0.35rem 0.8rem !important;
  font-weight: 500;
}

/* Override .btn-outline-secondary's #6c757d (4.37:1 on the header) with #495057. */
.ai-chat-header-actions .ai-chat-new-chat-btn {
  color: #495057;
  border-color: #495057;
}

.ai-chat-header-actions .ai-chat-new-chat-btn:hover,
.ai-chat-header-actions .ai-chat-new-chat-btn:focus,
.ai-chat-header-actions .ai-chat-new-chat-btn:active {
  color: #fff;
  background-color: #495057;
  border-color: #495057;
}

.ai-chat-header-actions .ai-chat-new-chat-btn:focus {
  box-shadow: 0 0 0 0.2rem rgba(73, 80, 87, 0.4);
}

.ai-chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem 0;
  background: #fff;
  min-height: 200px;
}

.ai-chat-empty {
  font-size: 16px;
  line-height: 1.5;
  color: #495057;
  padding: 1.5rem 1.25rem !important;
}

.ai-chat-fade-enter-active,
.ai-chat-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.ai-chat-fade-enter,
.ai-chat-fade-leave-to {
  opacity: 0;
  transform: translateY(6px);
}

.ai-chat-messages ::v-deep table {
  display: block;
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border-collapse: collapse;
  margin-bottom: 1rem;
}

.ai-chat-messages ::v-deep table th,
.ai-chat-messages ::v-deep table td {
  white-space: nowrap;
}

@media (max-width: 1024px) {
  .global-ai-chat {
    bottom: 5rem;
  }
}

@media (max-width: 768px) {
  .ai-chat-panel {
    width: calc(100vw - 32px) !important;
    height: 65vh !important;
    max-height: calc(100vh - 140px) !important;
  }

  .ai-chat-resize-handle {
    display: none !important;
  }
}

@media (max-width: 576px) {
  .ai-chat-launcher-pill,
  .ai-chat-launcher-btn-img {
    height: 2.85rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .ai-chat-launcher-shimmer {
    animation: none;
    opacity: 0;
  }
}
</style>
